/**
 * Зоны доставки исполнителя.
 *
 * Зон может быть несколько — например, ближний район дешевле дальнего. Если
 * они накладываются, клиенту считается более дешёвая, и об этом сказано прямо
 * на экране: иначе исполнитель поставит наоборот и будет недоумевать, почему
 * из центра приходит цена дальнего кольца.
 */
import type { CurrentUser, VendorZone } from '@arbuz/shared';
import { formatArea, formatMoney, makeT } from '../i18n';
import { haptic } from '../tg';
import { secondaryButton } from './courierUi';
import { EmptyState, hintStyle } from './vendorUi';

interface Props {
  user: CurrentUser;
  zones: VendorZone[] | null;
  error: string | null;
  onOpen: (id: number | 'new') => void;
  onRetry: () => void;
  onBack: () => void;
}

export function VendorZones({ user, zones, error, onOpen, onRetry, onBack }: Props) {
  const t = makeT(user.locale);

  return (
    <main className="page">
      <h1 className="h1">{t('shopSectionZones')}</h1>

      {error && (
        <EmptyState
          tone="error"
          title={t('zonesLoadError')}
          text={error}
          actionLabel={t('retry')}
          onAction={onRetry}
        />
      )}

      {!error && zones === null && <p className="muted">{t('loading')}</p>}

      {!error && zones !== null && zones.length === 0 && (
        <EmptyState
          title={t('zonesEmpty')}
          text={t('zonesEmptyHint')}
          actionLabel={t('zoneAdd')}
          onAction={() => onOpen('new')}
        />
      )}

      {!error && zones !== null && zones.length > 0 && (
        <>
          <ul
            style={{
              listStyle: 'none',
              margin: 'var(--s-4) 0 0',
              padding: 0,
              display: 'grid',
              gap: 'var(--s-2)',
            }}
          >
            {zones.map((z) => (
              <li key={z.id}>
                <button
                  type="button"
                  onClick={() => {
                    haptic('light');
                    onOpen(z.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--s-3)',
                    width: '100%',
                    minHeight: 'var(--tap-min)',
                    padding: 'var(--s-3)',
                    borderRadius: 'var(--r-lg)',
                    border: '1px solid var(--c-border)',
                    background: 'var(--c-surface)',
                    color: 'var(--c-text)',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    opacity: z.is_active ? 1 : 0.6,
                  }}
                >
                  <span>
                    <span
                      style={{
                        display: 'block',
                        font: 'var(--fw-medium) var(--fs-body)/var(--lh-body) var(--font)',
                      }}
                    >
                      {z.name}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        marginTop: 'var(--s-1)',
                        fontSize: 'var(--fs-sm)',
                        color: 'var(--c-text-secondary)',
                      }}
                    >
                      {formatMoney(z.delivery_price, user.locale)} · {z.points_count}{' '}
                      {t('zonePoints')} · {formatArea(z.area_km2, user.locale)}
                      {!z.is_active && ` · ${t('scheduleDayOff')}`}
                    </span>
                  </span>
                  <span aria-hidden="true" style={{ color: 'var(--c-text-tertiary)' }}>
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {zones.length > 1 && (
            <p style={{ ...hintStyle, marginTop: 'var(--s-3)' }}>{t('zoneCheaperHint')}</p>
          )}
        </>
      )}

      <div style={{ display: 'grid', gap: 'var(--s-2)', marginTop: 'var(--s-4)' }}>
        <button type="button" className="btn-primary" onClick={() => onOpen('new')}>
          {t('zoneAdd')}
        </button>
        <button type="button" style={secondaryButton} onClick={onBack}>
          {t('shopTitle')}
        </button>
      </div>
    </main>
  );
}
